import { useTranslation } from "react-i18next";
import { Divider, Form, Icon } from "semantic-ui-react";

import { BrandGroupSelect } from "components";
import { Element } from "components/ReactScroll/index";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { DMSIDS, DMS_CAPABILITIES } from "models";
import { usePlanitEstablishmentsQuery } from "modules/DealerLocationDetails/components/Service/hooks";
import { ASTERISK_PLACEHOLDER } from "modules/DealerOverviewDashboard/components";
import { isAdmin } from "utils/common";

import type { Dealer, DealerLocation } from "models";
import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

type ServiceProps = {
  tabId: string;
  location: DealerLocation;
  dealer?: Dealer;
};

export const Service = ({ tabId, location, dealer }: ServiceProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { data: establishmentOptions = [], isFetching: isFetchingEstablishments } = usePlanitEstablishmentsQuery(dealer);
  const { watch, setValue, formState } = FormHelper.useFormContext();
  const [isAcsesEnabled, isLefEnabled, isPinVisible, acsesPassword, isWarrantyPinSignatureMissingWarningEnabled, warrantyPinSignatureBrandGroupIds] = watch([
    "is_acses_enabled",
    "is_lef_enabled",
    "pin_visible",
    "acses_password",
    "is_warranty_pin_signature_missing_warning_enabled",
    "warranty_pin_signature_brand_group_ids"
  ]);

  const isAdminUser = Boolean(user && isAdmin(user.role_id));
  const isPlanitEnabled = Boolean(dealer?.planit_planning_enabled);
  const isTruckVision = location.dms_id === DMSIDS.TRUCK_VISION;
  const canWriteBackApkDate = location.dms_capability_ids?.includes(DMS_CAPABILITIES.WriteBackAPKDate);
  const isAcsesPasswordInvalid = formState.isSubmitted && isAcsesEnabled && !location.has_acses_password && !acsesPassword;

  return (
    <Element name={tabId} className="dealer-location-section">
      <div className="section-title">
        <div>
          <Icon className="wrench" />
        </div>
        <span className="section-title-text">{t("v8_service", "Service")}</span>
      </div>

      {isPlanitEnabled && (
        <>
          <div className="dealer-location-subheading">{t("v8_planit_planning", "PlanIT planning")}</div>
          <Form.Group>
            <Form.Field width={8}>
              <label>{t("v8_establishment_id", "Establishment ID")}</label>
              <FormHelper.Field.Dropdown
                name="planit_planning_establishment_id"
                options={establishmentOptions}
                dropdownProps={{ clearable: true, loading: isFetchingEstablishments }}
              />
            </Form.Field>
            <Form.Field width={8}>
              <FormHelper.Field.Checkbox name="planit_planning_replacement_vehicle" label={t("v8_show_replacement_car", "Show replacement car")} />
            </Form.Field>
          </Form.Group>
        </>
      )}

      {isTruckVision && (
        <>
          <div className="dealer-location-subheading">{t("v8_truck_vision_web_clock", "TruckVision WebClock")}</div>
          <Form.Group>
            <Form.Field width={8}>
              <label>Omgeving ID</label>
              <FormHelper.Field.Input nullable name="web_clock_omgeving_id" inputProps={{ type: "number", min: 1 }} />
            </Form.Field>
          </Form.Group>
        </>
      )}

      {isAdminUser && (
        <>
          {(isPlanitEnabled || isTruckVision) && <Divider />}
          <div className="dealer-location-columns">
            <div className="dealer-location-column">
              <div className="dealer-location-subheading">{t("v8_pin_configuration", "Pin Configuration")}</div>
              <Form.Field>
                <FormHelper.Field.Checkbox name="pin_visible" label={t("v8_pinning", "Pinning")} />
              </Form.Field>
              {isPinVisible && (
                <>
                  <Form.Field>
                    <FormHelper.Field.Checkbox
                      name="is_warranty_pin_signature_missing_warning_enabled"
                      label={t("v8_warranty_signature_missing_red_warning", "Warranty signature missing (Red warning)")}
                    />
                  </Form.Field>
                  {isWarrantyPinSignatureMissingWarningEnabled && (
                    <Form.Field>
                      <BrandGroupSelect
                        value={warrantyPinSignatureBrandGroupIds}
                        onChange={brandGroupIds => setValue("warranty_pin_signature_brand_group_ids", brandGroupIds)}
                      />
                    </Form.Field>
                  )}

                  <div className="dealer-location-subheading">{t("v8_pin_warranty_options", "Pin warranty options")}</div>
                  <Form.Field>
                    <FormHelper.Field.Checkbox name="is_warranty_pin_support_warning_enabled" label={t("v8_missing_support_nr", "Missing support # (Orange warning)")} />
                  </Form.Field>
                  <Form.Field>
                    <FormHelper.Field.Checkbox name="is_warranty_pin_claim_warning_enabled" label={t("v8_missing_claim_nr", "Missing claim # (Orange warning)")} />
                  </Form.Field>

                  <div className="dealer-location-subheading">{t("v8_pin_recall_options", "Pin recall options")}</div>
                  <Form.Field>
                    <FormHelper.Field.Checkbox name="is_recall_pin_support_warning_enabled" label={t("v8_missing_support_nr", "Missing support # (Orange warning)")} />
                  </Form.Field>
                  <Form.Field>
                    <FormHelper.Field.Checkbox name="is_recall_pin_claim_warning_enabled" label={t("v8_missing_claim_nr", "Missing claim # (Orange warning)")} />
                  </Form.Field>
                </>
              )}

              <div className="dealer-location-subheading">{t("v8_rdw_configuration", "RDW configuration")}</div>
              <Form.Field>
                <FormHelper.Field.Checkbox name="upgrade_car_with_rdw" label={t("v8_upgrade_car", "Upgrade car")} />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox name="upgrade_with_rdw_apk_date" label={t("v8_upgrade_car_with_apk_date", "Upgrade car with APK date")} />
              </Form.Field>
              {canWriteBackApkDate && (
                <Form.Field>
                  <FormHelper.Field.Checkbox name="write_back_rdw_apk_date" label={t("v8_write_back_rdw_apk_date", "Writeback RDW APK date")} />
                </Form.Field>
              )}
            </div>

            <div className="dealer-location-column">
              <div className="dealer-location-subheading">{t("v8_distrimate_recalls_configuration", "Distrimate Recalls Configuration")}</div>
              <Form.Field>
                <FormHelper.Field.Checkbox name="is_recalls_service_enabled" label={t("v8_import_recalls", "Import recalls")} />
              </Form.Field>

              <div className="dealer-location-subheading">{t("v8_lef_configuration", "LEF Configuration")}</div>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="is_lef_enabled"
                  label={t("v8_send_leads_to_lef", "Send leads to LEF")}
                  onChange={enabled => !enabled && setValue("lef_account_number", "")}
                />
              </Form.Field>
              <Form.Field>
                <label>{t("v8_account_number", "Account number")}</label>
                <FormHelper.Field.Input name="lef_account_number" inputProps={{ disabled: !isLefEnabled }} />
              </Form.Field>
            </div>
          </div>

          <Divider />
          <div className="dealer-location-subheading">{t("v8_acses_configuration", "Acses Configuration")}</div>
          <Form.Group>
            <Form.Field width={16}>
              <FormHelper.Field.Checkbox name="is_acses_enabled" label={t("v8_acses", "ACSES")} />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={8} required={isAcsesEnabled} error={formState.isSubmitted && isAcsesEnabled && !watch("acses_email")}>
              <label>{t("v8_acses_email", "Acses email")}</label>
              <FormHelper.Field.Input name="acses_email" />
            </Form.Field>
            <Form.Field width={8} required={isAcsesEnabled} error={isAcsesPasswordInvalid}>
              <label>{t("v8_acses_password", "Acses password")}</label>
              <FormHelper.Field.Input
                name="acses_password"
                preventAutofill
                inputProps={{ type: "password", placeholder: location.has_acses_password ? ASTERISK_PLACEHOLDER : "" }}
              />
            </Form.Field>
          </Form.Group>
        </>
      )}
    </Element>
  );
};
